<template>
  <div>
    <div v-if="$auth.policies['view-meal-config']">
      <div class="pa-4 d-flex justify-space-between align-center">
        <Breadcrumbs headline="Khóa đăng ký ăn" :link="[{ text: 'Dịch vụ bếp', href: '#' }, { text: 'Khóa đăng ký ăn' }]" />
        <v-btn depressed dark color="#0D47A1" @click="openCreate"><v-icon left>add</v-icon>Thêm khóa</v-btn>
      </div>

      <v-card outlined class="mx-4 elevation-0">
        <v-data-table :headers="headers" :items="locks" :loading="loading" :items-per-page="20">
          <template v-slot:[`item.meal`]="{ item }">{{ slotOf(item).title }}</template>
          <template v-slot:[`item.timeOfDay`]="{ item }">{{ timeOfDayLabel(slotOf(item).timeOfDay) }}</template>
          <template v-slot:[`item.serveDays`]="{ item }">{{ serveDaysLabel(slotOf(item).serveDays) }}</template>
          <template v-slot:[`item.group`]="{ item }">{{ groupOf(item).title }}</template>
          <template v-slot:[`item.fromDate`]="{ item }">{{ item.fromDate | ddmmyyyy }}</template>
          <template v-slot:[`item.toDate`]="{ item }">{{ item.toDate ? $options.filters.ddmmyyyy(item.toDate) : NO_END_DATE_LABEL }}</template>
          <template v-slot:[`item.action`]="{ item }">
            <v-btn icon small color="primary" title="Chi tiết" :to="`/meal-lock-detail/${item.id}`">
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>

      <!-- Dialog thêm/sửa khóa -->
      <v-dialog v-model="dialog" width="600" :fullscreen="$vuetify.breakpoint.smAndDown">
        <v-card>
          <v-card-title class="primary white--text">
            <span>THÊM KHÓA</span><v-spacer /><v-icon color="white" @click="dialog = false">close</v-icon>
          </v-card-title>
          <v-form ref="form" class="pa-4" @keydown.native.enter.prevent="save">
            <v-autocomplete
              v-model="form.meal_slot"
              :items="fixedMeals"
              item-text="title"
              item-value="id"
              :label="MEAL_TYPE_FULL_LABEL.fixed"
              outlined
              dense
              :rules="[$rules.required]"
            />
            <v-autocomplete
              v-model="form.meal_group"
              :items="groups"
              item-text="title"
              item-value="id"
              label="Nhóm đối tượng bị khóa"
              hint="Có thể chọn bất kỳ nhóm: toàn trường / cấp / khối / lớp / nhóm tự tạo"
              persistent-hint
              outlined
              dense
              :rules="[$rules.required]"
            />
            <v-row class="mt-1">
              <v-col cols="12" md="6">
                <DateIOSPicker label="Từ ngày *" outlined dense :date.sync="form.fromDate" />
              </v-col>
              <v-col cols="12" md="6">
                <DateIOSPicker label="Đến ngày (tùy chọn)" outlined dense :date.sync="form.toDate" />
              </v-col>
            </v-row>
          </v-form>
          <v-alert dense text type="info" class="caption mx-4 mb-0">{{ effectNote }}</v-alert>
          <v-card-actions>
            <v-btn outlined @click="dialog = false">Hủy</v-btn>
            <v-spacer />
            <v-btn color="primary" depressed :loading="saving" @click="save">Lưu</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
    <Page403 v-else />
  </div>
</template>

<script>
import Breadcrumbs from '@/components/layout/Breadcrumbs'
import Page403 from '@/views/Page403.vue'
import DateIOSPicker from '@/components/basic/picker/DateIOSPicker.vue'
import { MealSlot, MealGroup } from '@/plugins/api'
import { mapActions, mapState } from 'vuex'
import moment from 'moment'
import {
  timeOfDayLabel,
  serveDaysLabel,
  MEAL_TYPE_FULL_LABEL,
  NO_END_DATE_LABEL,
  EFFECT_NOTE
} from '@/modules/mealConfig/mealConfigConstants'

export default {
  components: { Breadcrumbs, Page403, DateIOSPicker },
  filters: {
    ddmmyyyy(v) {
      return v ? moment(v).format('DD/MM/YYYY') : ''
    }
  },
  data() {
    return {
      dialog: false,
      saving: false,
      fixedMeals: [],
      groups: [],
      MEAL_TYPE_FULL_LABEL,
      NO_END_DATE_LABEL,
      effectNote: EFFECT_NOTE,
      form: { id: null, meal_slot: null, meal_group: null, fromDate: null, toDate: null },
      headers: [
        { text: 'Tên bữa', value: 'meal', sortable: false },
        { text: 'Thời điểm', value: 'timeOfDay', sortable: false },
        { text: 'Ngày phục vụ', value: 'serveDays', sortable: false },
        { text: 'Nhóm bị khóa', value: 'group', sortable: false },
        { text: 'Từ ngày', value: 'fromDate', sortable: false },
        { text: 'Đến ngày', value: 'toDate', sortable: false },
        { text: 'Thao tác', value: 'action', sortable: false, align: 'center' }
      ]
    }
  },
  computed: {
    ...mapState('mealLock', ['locks', 'loading'])
  },
  created() {
    this.refresh()
  },
  methods: {
    ...mapActions('mealLock', ['fetchList', 'create']),
    timeOfDayLabel,
    serveDaysLabel,
    slotOf(lock) {
      return lock.meal_slot || {}
    },
    groupOf(lock) {
      return lock.meal_group || {}
    },
    refresh() {
      return this.fetchList().catch(e => this.$alert.error('Không tải được danh sách khóa: ' + e))
    },
    async loadOptions() {
      const [fixedMeals, groups] = await Promise.all([
        MealSlot.fetch({ type: 'fixed', _limit: -1 }),
        MealGroup.fetch({ _limit: -1, _sort: 'title:ASC' })
      ])
      this.fixedMeals = fixedMeals
      this.groups = groups
    },
    async openCreate() {
      await this.loadOptions()
      this.form = { id: null, meal_slot: null, meal_group: null, fromDate: moment().add(1, 'day').format('YYYY-MM-DD'), toDate: null }
      this.dialog = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    },
    async save() {
      if (!this.$refs.form.validate()) return
      this.saving = true
      try {
        await this.create({
          meal_slot: this.form.meal_slot,
          meal_group: this.form.meal_group,
          fromDate: this.form.fromDate,
          toDate: this.form.toDate || null
        })
        this.$alert.addSuccess()
        this.dialog = false
        this.refresh()
      } catch (err) {
        this.$alert.error('Lưu khóa thất bại: ' + (err.response ? err.response.data.message : err))
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
